import React, {useEffect, useRef} from 'react'

// Paleta em RGB para compor com alpha variável
const COLORS = ['0,180,216', '0,180,216', '72,149,239', '230,57,70']
const LINK_DIST = 130
const MOUSE_DIST = 170

type Node = {
  x: number
  y: number
  vx: number
  vy: number
  r: number
  color: string
  phase: number
  speed: number
  wander: number
}

/**
 * Constelação de partículas em canvas: pontos que flutuam, cintilam, se
 * conectam por linhas quando próximos e reagem ao cursor.
 */
export default function ParticleField() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    let w = 0
    let h = 0
    let nodes: Node[] = []
    let raf = 0
    const mouse = {x: -9999, y: -9999, active: false}

    const spawn = (): Node => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.7,
      vy: (Math.random() - 0.5) * 0.7 - 0.15,
      r: Math.random() * 1.8 + 0.8,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      phase: Math.random() * Math.PI * 2,
      speed: Math.random() * 0.03 + 0.012,
      wander: Math.random() * Math.PI * 2,
    })

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      w = rect.width
      h = rect.height
      canvas.width = Math.round(w * dpr)
      canvas.height = Math.round(h * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      const target = Math.min(110, Math.round((w * h) / 13000))
      while (nodes.length < target) nodes.push(spawn())
      nodes.length = target
    }

    const draw = () => {
      ctx.clearRect(0, 0, w, h)

      for (const n of nodes) {
        // Leve repulsão do cursor
        if (mouse.active) {
          const dx = n.x - mouse.x
          const dy = n.y - mouse.y
          const d2 = dx * dx + dy * dy
          if (d2 < MOUSE_DIST * MOUSE_DIST && d2 > 1) {
            const f = (1 - Math.sqrt(d2) / MOUSE_DIST) * 0.6
            n.x += (dx / Math.sqrt(d2)) * f
            n.y += (dy / Math.sqrt(d2)) * f
          }
        }
        // Trajetória levemente ondulada em vez de linha reta
        n.wander += 0.01
        n.x += n.vx + Math.cos(n.wander) * 0.25
        n.y += n.vy + Math.sin(n.wander * 0.8) * 0.25
        n.phase += n.speed
        // Reaparece do lado oposto ao sair da tela
        if (n.x < -20) n.x = w + 20
        else if (n.x > w + 20) n.x = -20
        if (n.y < -20) n.y = h + 20
        else if (n.y > h + 20) n.y = -20
      }

      // Conexões entre partículas próximas
      ctx.lineWidth = 0.7
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i]
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j]
          const dx = a.x - b.x
          const dy = a.y - b.y
          const d2 = dx * dx + dy * dy
          if (d2 < LINK_DIST * LINK_DIST) {
            const alpha = (1 - Math.sqrt(d2) / LINK_DIST) * 0.28
            ctx.strokeStyle = `rgba(${a.color},${alpha})`
            ctx.beginPath()
            ctx.moveTo(a.x, a.y)
            ctx.lineTo(b.x, b.y)
            ctx.stroke()
          }
        }
        // Conexões com o cursor
        if (mouse.active) {
          const dx = a.x - mouse.x
          const dy = a.y - mouse.y
          const d = Math.sqrt(dx * dx + dy * dy)
          if (d < MOUSE_DIST) {
            ctx.strokeStyle = `rgba(${a.color},${(1 - d / MOUSE_DIST) * 0.45})`
            ctx.beginPath()
            ctx.moveTo(a.x, a.y)
            ctx.lineTo(mouse.x, mouse.y)
            ctx.stroke()
          }
        }
      }

      // Partículas com brilho cintilante
      for (const n of nodes) {
        const twinkle = 0.55 + Math.sin(n.phase) * 0.45
        const glow = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r * 5)
        glow.addColorStop(0, `rgba(${n.color},${0.35 * twinkle})`)
        glow.addColorStop(1, `rgba(${n.color},0)`)
        ctx.fillStyle = glow
        ctx.beginPath()
        ctx.arc(n.x, n.y, n.r * 5, 0, Math.PI * 2)
        ctx.fill()

        ctx.fillStyle = `rgba(${n.color},${0.45 + 0.5 * twinkle})`
        ctx.beginPath()
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2)
        ctx.fill()
      }

      raf = requestAnimationFrame(draw)
    }

    const onMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect()
      mouse.x = e.clientX - rect.left
      mouse.y = e.clientY - rect.top
      mouse.active = mouse.x >= 0 && mouse.y >= 0 && mouse.x <= w && mouse.y <= h
    }
    const onLeave = () => {
      mouse.active = false
    }

    const ro = new ResizeObserver(resize)
    ro.observe(canvas)
    resize()
    draw()
    window.addEventListener('pointermove', onMove)
    document.addEventListener('pointerleave', onLeave)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      window.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerleave', onLeave)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className='absolute inset-0 h-full w-full'
      style={{
        // Suaviza as bordas para as partículas sumirem perto do header/sidebar
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 55%, transparent 100%)',
        maskImage: 'radial-gradient(ellipse at center, black 55%, transparent 100%)',
      }}
    />
  )
}
